import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../utils/Api";
import foodImage from "../assets/gallery3.avif";

const WHATSAPP_NUMBER = "447368325136";

// Shared Tailwind classes
const infoCard = "bg-gradient-to-b from-[#111827] to-[#0f172a] border border-white/[0.08] rounded-[20px] p-[18px] shadow-[0_10px_30px_rgba(0,0,0,0.25)]";
const detailRow = "flex items-center gap-3 py-3 border-t border-white/[0.06] first:border-t-0 first:pt-0";
const detailIcon = "w-10 h-10 min-w-[40px] rounded-xl flex items-center justify-center bg-white/[0.06] text-[#f8fafc] text-[15px]";
const detailTextWrap = "flex flex-col gap-0.5 min-w-0";

export default function OrderTracking() {
  const { apiFetch } = useApi();
  const [status, setStatus] = useState("to do");
  const [orderId, setOrderId] = useState("");
  const [orderType, setOrderType] = useState("");
  const [statusSource, setStatusSource] = useState("local");
  const [driver, setDriver] = useState(null);
  const [eta, setEta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const encryptedId = searchParams.get("id");

  const formatApiStatus = (value) => String(value || "").replace(/[_-]+/g, " ").trim().replace(/\b\w/g, (c) => c.toUpperCase());
  const formatEtaOnlyMinutes = (value) => {
    if (!value) return "";
    const str = String(value).trim(); const firstPart = str.split(",")[0]?.trim();
    return (/min/i.test(firstPart) ? firstPart : firstPart) || str;
  };

  useEffect(() => {
    if (!encryptedId) { setError("Missing order ID in URL"); setLoading(false); return; }
    let interval = null; let isMounted = true; let inFlight = false; let firstLoad = true;
    async function fetchStatus(forceRefresh = false) {
      if (inFlight) return; inFlight = true;
      try {
        const data = await apiFetch("order-status", { encrypted_order_id: encryptedId, force_refresh: forceRefresh ? 1 : 0 });
        if (!isMounted) return;
        if (!data.success || !data.order) { if (firstLoad) { setError(data.message || "Invalid order"); setLoading(false); } return; }
        const order = data.order;
        setStatus(String(order.status || "").trim());
        setOrderId(order.id || ""); setOrderType(String(order.order_type || "").toLowerCase().trim());
        setStatusSource(String(order.status_source || "local").toLowerCase().trim());
        setDriver(order.driver || null); setEta(order.eta || null);
        setLoading(false); setError(null); firstLoad = false;
      } catch (err) {
        if (!isMounted) return; console.error("Failed to fetch order:", err);
        if (firstLoad) { setError("Network or server error"); setLoading(false); }
      } finally { inFlight = false; firstLoad = false; }
    }
    fetchStatus(true); interval = setInterval(() => fetchStatus(false), 10000);
    return () => { isMounted = false; if (interval) clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encryptedId]);

  const isDelivery = orderType === "delivery";
  const lowerStatus = String(status || "").toLowerCase().trim();
  const showOriginalThreeStepDesign = !isDelivery || (isDelivery && (statusSource === "local" || statusSource === "handoff"));
  const steps = [
    { key: "to do", label: "Order Received", subtext: "Your order is in queue" },
    { key: "in progress", label: "Being Prepared", subtext: "Your order is being prepared." },
    { key: "completed", label: isDelivery ? "Order Ready for Delivery" : "Order Ready", subtext: isDelivery ? "Your order is ready for delivery." : "Your order is ready for collection." },
  ];
  const originalTrackingConfig = useMemo(() => {
    const stepOrder = { "to do": 0, "in progress": 1, completed: 2 };
    const currentStep = stepOrder[lowerStatus] ?? 0;
    const progressMap = { "to do": "20%", "in progress": "60%", completed: "100%" };
    return { currentStep, progress: progressMap[lowerStatus] || "20%" };
  }, [lowerStatus]);

  const showDriverDetails = isDelivery && statusSource === "naijameals" && !["done", "completed", "delivered"].includes(lowerStatus) && (driver || eta?.pickup || eta?.dropoff);

  const pageBase = "min-h-screen bg-black text-white p-5 max-w-[420px] mt-[110px] mx-auto font-[system-ui,-apple-system,BlinkMacSystemFont]";

  if (loading) return <div className={pageBase}><div className="flex justify-center items-center py-[60px] text-lg">Loading order status...</div></div>;
  if (error) return <div className={pageBase}><div className="text-[#b00020] py-10 text-center font-medium">Error: {error}</div></div>;

  if (lowerStatus === "cancelled") {
    return (
      <div className={pageBase}>
        <header className="flex items-center gap-3 flex-wrap mb-2.5 border-b border-[rgba(255,82,82,0.4)]">
          <h1 className="text-[#ff5252] flex items-center gap-2.5 [text-shadow:0_0_8px_rgba(255,82,82,0.6)]"><i className="fas fa-times-circle" /> Order Cancelled</h1>
        </header>
        <p className="text-sm text-[#b3b3b3] mb-3.5">Order ID: <span className="text-white">L-{orderId}</span></p>
        <div className="mt-6 px-5 py-4 bg-[rgba(255,82,82,0.12)] border border-[rgba(255,82,82,0.5)] rounded-[10px] text-[#ff5252] font-medium text-center shadow-[0_0_15px_rgba(255,82,82,0.25)]">This order will not be processed.</div>
        <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 my-3 font-semibold text-[15px] no-underline text-[#248648] hover:underline hover:-translate-y-px transition-all">
          <i className="fab fa-whatsapp text-lg" /> Click to chat with us on WhatsApp
        </a>
        <div className="mt-5 rounded-[18px] overflow-hidden bg-[#111]"><img src={foodImage} alt="Order" className="w-full block" /></div>
      </div>
    );
  }

  return (
    <div className={pageBase}>
      <header className="flex items-center gap-3 flex-wrap mb-2.5">
        <h1 className="text-[18px] font-semibold">Order ID: L-{orderId}</h1>
      </header>

      {showOriginalThreeStepDesign && (
        <>
          <div className="w-full bg-[#eee] rounded-lg my-4 h-3 overflow-hidden">
            <div className="h-full relative">
              <div className="h-full bg-[#4caf50] transition-[width] duration-500 rounded-lg" style={{ width: originalTrackingConfig.progress }} />
            </div>
          </div>
          <div className="bg-gradient-to-b from-[#111] to-[#0b0b0b] rounded-[14px] p-4 mb-2.5">
            {steps.map((step, index) => {
              const isActive = index <= originalTrackingConfig.currentStep;
              return (
                <div key={step.key} className={`flex items-center gap-2.5 mb-3 last:mb-0 transition-opacity ${isActive ? "opacity-100" : "opacity-50"}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${isActive ? "bg-[#1db954]" : "bg-[#555]"}`} />
                  <div>
                    <p className="text-sm m-0">{step.label}</p>
                    <small className="text-xs text-[#888] mt-0.5 block">{step.subtext}</small>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {isDelivery && statusSource === "naijameals" && !showOriginalThreeStepDesign && (
        <div className="bg-gradient-to-b from-[#111] to-[#0b0b0b] rounded-[14px] p-4 mb-2.5">
          <div className="flex items-center gap-2.5 mb-3">
            <span className="w-2.5 h-2.5 rounded-full bg-[#1db954]" />
            <div>
              <p className="text-sm m-0">{formatApiStatus(status)}</p>
              {!["done", "completed", "delivered"].includes(lowerStatus) && <small className="text-xs text-[#888] mt-0.5 block">Your delivery is being tracked.</small>}
            </div>
          </div>
        </div>
      )}

      {showDriverDetails && (
        <div className={`${infoCard} mt-4`}>
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[rgba(168,85,247,0.12)] text-[#d8b4fe] text-[17px]"><i className="fas fa-truck" /></div>
            <h3 className="m-0 text-white text-[22px]">Delivery Details</h3>
          </div>
          {driver ? (
            <div className="flex flex-col gap-3">
              {[
                { icon: "fas fa-user", label: "Rider Name", value: driver.name || "Not assigned yet" },
                { icon: "fas fa-phone-alt", label: "Phone", value: driver.phone ? <a href={`tel:${driver.phone}`} className="text-inherit no-underline font-semibold hover:underline">{driver.phone}</a> : "Not available" },
                { icon: "fas fa-bicycle", label: "Vehicle", value: driver.transport_type || "N/A" },
                ...(eta?.pickup ? [{ icon: "fas fa-store", label: "Estimated rider pick-up", value: formatEtaOnlyMinutes(eta.pickup) }] : []),
                ...(eta?.dropoff ? [{ icon: "fas fa-clock", label: "Estimated delivery", value: formatEtaOnlyMinutes(eta.dropoff) }] : []),
              ].map(({ icon, label, value }, i) => (
                <div key={i} className={detailRow}>
                  <div className={detailIcon}><i className={icon} /></div>
                  <div className={detailTextWrap}>
                    <span className="text-[#94a3b8] text-sm">{label}</span>
                    <strong className="text-white text-[17px] leading-snug break-words">{value}</strong>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2.5 text-[#cbd5e1] text-[15px]">
              <i className="fas fa-user-clock text-[#f8fafc]" />
              <p>Driver has not been assigned yet.</p>
            </div>
          )}
          <div className="text-right text-[0.8rem] mt-1.5 text-[#f59e0b] font-medium">
            Powered by <a href="https://naijameals.com" target="_blank" rel="noopener noreferrer" className="text-[#f59e0b] font-semibold underline hover:font-bold">Naijameals.com</a>
          </div>
        </div>
      )}

      {!isDelivery && lowerStatus === "completed" && (
        <div className="mt-4 p-3.5 bg-[#1db954] text-black rounded-xl text-sm font-medium text-center">Your order is ready for collection.</div>
      )}

      <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 my-3 font-semibold text-[15px] no-underline text-[#248648] hover:underline hover:-translate-y-px transition-all">
        <i className="fab fa-whatsapp text-lg" /> Click to chat with us on WhatsApp
      </a>

      <div className="mt-5 rounded-[18px] overflow-hidden bg-[#111]">
        <img src={foodImage} alt="Order" className="w-full block" />
      </div>
    </div>
  );
}
